import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Layers, Link2, X, Search, PackageOpen, Copy,
} from 'lucide-react';
import api from '../lib/api';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useMoney } from '../context/CurrencyContext';
import { computeLine, buildMastersDict } from '../lib/engine';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { NumberStepper } from '../components/NumberStepper';
import { EmptyState } from '../components/common';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from '../components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '../components/ui/command';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const MTYPES = ['material', 'manpower', 'supervisors', 'vehicle', 'tools', 'subcontractor', 'safety', 'misc'];
const MTYPE_TO_CAT = {
  material: 'MATERIAL', vehicle: 'VEHICLE', manpower: 'MANPOWER', supervisors: 'SUPERVISORS',
  tools: 'TOOLS & EQUIPMENT', safety: 'SAFETY', subcontractor: 'SUBCONTRACTOR', misc: 'MISC',
};
// Fixed discipline list — activities are grouped by these (like Rate Master tabs).
const DISCIPLINES = [
  'High Voltage (HV)', 'Electrical LV', 'Mechanical', 'HVAC', 'Plumbing',
  'Fire Fighting', 'Fire Alarm', 'ELV / Low Current', 'Instrumentation & Control', 'Civil',
];
const CAT_TONE = {
  MATERIAL: 'text-[hsl(var(--copper))] border-[hsl(var(--copper)/0.4)] bg-[hsl(var(--copper)/0.08)]',
  MANPOWER: 'text-[hsl(var(--teal))] border-[hsl(var(--teal)/0.4)] bg-[hsl(var(--teal)/0.08)]',
  SUPERVISORS: 'text-[hsl(var(--brand))] border-[hsl(var(--brand)/0.4)] bg-[hsl(var(--brand)/0.08)]',
};
const catTone = (c) => CAT_TONE[c] || 'text-muted-foreground border-border bg-muted/40';

// Resolve a rate code across all master types → { category, rate(AED), description, unit }.
function resolveCode(code, mastersByType) {
  const cu = String(code || '').toUpperCase();
  for (const t of MTYPES) {
    const item = mastersByType[t]?.[cu];
    if (item) return { mtype: t, category: MTYPE_TO_CAT[t], item };
  }
  return null;
}

// Combobox to pick any Rate Master item (across all 8 types).
function RatePicker({ mastersByType, onPick }) {
  const [open, setOpen] = useState(false);
  const all = useMemo(() => {
    const out = [];
    for (const t of MTYPES) {
      for (const m of Object.values(mastersByType[t] || {})) {
        if (m.active === false) continue;
        out.push({ ...m, _cat: MTYPE_TO_CAT[t] });
      }
    }
    return out.sort((a, b) => String(a.code).localeCompare(String(b.code)));
  }, [mastersByType]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8" data-testid="activity-add-comp-line">
          <Link2 size={13} className="mr-1.5" />Add rate item
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[36rem] max-w-[92vw] p-0">
        <Command>
          <CommandInput placeholder="Search code, description or trade…" />
          <CommandList>
            <CommandEmpty>No rate items.</CommandEmpty>
            {all.map((m) => (
              <CommandItem key={`${m._cat}-${m.code}`} value={`${m.code} ${m.description || m.trade || ''}`}
                className="items-start gap-2"
                onSelect={() => { onPick(m.code); setOpen(false); }}>
                <span className="mt-0.5 shrink-0 font-num text-xs text-[hsl(var(--copper))]">{m.code}</span>
                <span className="min-w-0 flex-1 whitespace-normal break-words text-xs text-muted-foreground">{m.description || m.trade}</span>
                <span className={cn('mt-0.5 shrink-0 rounded border px-1 text-[9px] font-semibold uppercase', catTone(m._cat))}>{m._cat}</span>
                <span className="mt-0.5 shrink-0 font-num text-[10px] tabular-nums">{m.rate}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const emptyForm = { description: '', uom: '', discipline: '', system: '', composition: [] };

export default function Activities() {
  const { can } = useAuth();
  const { money, code: curCode } = useMoney();
  const canEdit = can('rate_masters', 'readwrite');
  const [rows, setRows] = useState([]);
  const [mastersByType, setMastersByType] = useState({});
  const [mastersDict, setMastersDict] = useState({});
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(DISCIPLINES[0]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [acts, ...masterRes] = await Promise.all([
        api.get('/activities'),
        ...MTYPES.map((t) => api.get(`/masters/${t}`)),
      ]);
      const byType = {};
      MTYPES.forEach((t, i) => { byType[t] = Object.fromEntries((masterRes[i].data || []).map((m) => [String(m.code).toUpperCase(), m])); });
      setMastersByType(byType);
      setMastersDict(buildMastersDict(Object.fromEntries(MTYPES.map((t, i) => [t, masterRes[i].data]))));
      setRows(acts.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live preview of the composition currently in the editor (fils, engine-computed).
  const preview = useMemo(() => {
    const split = {};
    let unit = 0;
    const lines = (form.composition || []).map((c) => {
      const r = resolveCode(c.rateCode, mastersByType);
      const cat = r?.category || 'MISC';
      const cl = computeLine({ code: c.rateCode, qtyPerUnit: c.qtyPerUnit }, 1, mastersDict, cat, false);
      split[cat] = (split[cat] || 0) + cl.amount;
      unit += cl.amount;
      return {
        ...c, category: cat, description: cl.description, unit: cl.unit,
        rate: cl.masterRate, lineCost: cl.amount, resolved: !!r,
      };
    });
    return { lines, split, unitCost: unit };
  }, [form.composition, mastersByType, mastersDict]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, discipline: tab }); setSheetOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      description: a.description || '', uom: a.uom || '', discipline: a.discipline || '',
      system: a.system || '',
      composition: (a.composition || []).map((c) => ({ rateCode: c.rateCode, qtyPerUnit: c.qtyPerUnit })),
    });
    setSheetOpen(true);
  };

  const addCompLine = (rateCode) => {
    setForm((f) => {
      if ((f.composition || []).some((c) => c.rateCode === rateCode)) {
        toast.info(`${rateCode} is already in this activity`);
        return f;
      }
      return { ...f, composition: [...(f.composition || []), { rateCode, qtyPerUnit: 1 }] };
    });
  };
  const setCompQty = (idx, v) => setForm((f) => {
    const composition = [...f.composition];
    composition[idx] = { ...composition[idx], qtyPerUnit: v };
    return { ...f, composition };
  });
  const removeCompLine = (idx) => setForm((f) => ({ ...f, composition: f.composition.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.discipline) { toast.error('Select a discipline'); return; }
    if (!form.composition.length) { toast.error('Add at least one rate item to the composition'); return; }
    setSaving(true);
    try {
      const payload = {
        description: form.description, uom: form.uom, discipline: form.discipline, system: form.system,
        composition: form.composition.map((c) => ({ rateCode: c.rateCode, qtyPerUnit: Number(c.qtyPerUnit) || 0 })),
      };
      if (editing) await api.put(`/activities/${editing.id}`, payload);
      else await api.post('/activities', payload);
      toast.success(editing ? 'Activity updated' : 'Activity created');
      setSheetOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const duplicate = async (a) => {
    try {
      await api.post('/activities', {
        description: `${a.description} (copy)`, uom: a.uom, discipline: a.discipline, system: a.system,
        composition: (a.composition || []).map((c) => ({ rateCode: c.rateCode, qtyPerUnit: Number(c.qtyPerUnit) || 0 })),
      });
      toast.success(`Duplicated ${a.activityCode}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/activities/${deleting.id}`);
      toast.success('Activity deleted');
      setDeleting(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const countByDiscipline = rows.reduce((acc, a) => {
    const d = a.discipline || '';
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});
  const filtered = rows.filter((a) => {
    if ((a.discipline || '') !== tab) return false;
    const hay = `${a.activityCode} ${a.description} ${a.discipline} ${a.system}`.toLowerCase();
    return hay.includes(q.toLowerCase().trim());
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-6" data-testid="activities-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Layers size={20} className="text-[hsl(var(--copper))]" />
            <h1 className="font-display text-2xl font-bold">Activity Master</h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Reusable activity templates built from Rate Master items (quantities only).
            Apply them to a Cost BOQ line to fill it with locked, live-priced rows.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} className="bg-[hsl(var(--brand))] text-white hover:brightness-110" data-testid="activity-new-btn">
            <Plus size={15} className="mr-1.5" />New Activity
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="-mx-4 max-w-full overflow-x-auto px-4 no-scrollbar sm:mx-0 sm:px-0">
            <TabsList className="w-max">
              {DISCIPLINES.map((d) => (
                <TabsTrigger key={d} value={d} data-testid={`activity-tab-${d}`}>
                  {d}{countByDiscipline[d] ? ` (${countByDiscipline[d]})` : ''}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="relative max-w-xs flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search activities…"
              className="pl-9" data-testid="activity-search" />
          </div>
        </div>

        {loading ? (
          <div className="mt-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !filtered.length ? (
          <div className="mt-8">
            <EmptyState testid="activities-empty" icon={PackageOpen}
              title={`No ${tab} activities yet`}
              description="Create a reusable activity template for this discipline from your Rate Master items."
              action={canEdit ? <Button onClick={openCreate}><Plus size={15} className="mr-1.5" />New Activity</Button> : null} />
          </div>
        ) : (
          <div className="erak-card mt-5 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="activities-table">
                <thead>
                  <tr className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="w-28 px-4 py-3 text-left font-semibold">Activity No.</th>
                    <th className="px-4 py-3 text-left font-semibold">Description</th>
                    <th className="w-20 px-4 py-3 text-left font-semibold">UoM</th>
                    <th className="px-4 py-3 text-left font-semibold">System</th>
                    <th className="w-24 px-4 py-3 text-right font-semibold">Lines</th>
                    <th className="w-40 px-4 py-3 text-right font-semibold">Unit Cost ({curCode})</th>
                    {canEdit && <th className="w-24 px-4 py-3 text-right font-semibold">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr key={a.id} data-testid={`activity-row-${a.activityCode}`} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-num font-semibold text-[hsl(var(--copper))]">{a.activityCode}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium" title={a.description}>{a.description}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{a.uom || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{a.system || '—'}</td>
                      <td className="px-4 py-3 text-right font-num tabular-nums text-muted-foreground">{(a.composition || []).length}</td>
                      <td className="px-4 py-3 text-right font-num font-bold tabular-nums" data-testid={`activity-unitcost-${a.activityCode}`}>{money(a.unitCost)}</td>
                      {canEdit && (
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-0.5">
                            <button data-testid={`activity-edit-${a.activityCode}`} onClick={() => openEdit(a)}
                              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-[hsl(var(--teal))]"><Pencil size={13} /></button>
                            <button data-testid={`activity-duplicate-${a.activityCode}`} title="Duplicate this activity" onClick={() => duplicate(a)}
                              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-[hsl(var(--copper))]"><Copy size={13} /></button>
                            <button data-testid={`activity-delete-${a.activityCode}`} onClick={() => setDeleting(a)}
                              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-[hsl(var(--danger))]"><Trash2 size={13} /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Tabs>

      {/* Create / edit sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-2xl" data-testid="activity-editor-sheet">
          <SheetHeader>
            <SheetTitle>{editing ? `Edit ${editing.activityCode}` : 'New Activity'}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="e.g. 11kV Transformer Install" data-testid="activity-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>UoM</Label><Input value={form.uom} onChange={(e) => setForm((f) => ({ ...f, uom: e.target.value }))} placeholder="nos" data-testid="activity-form-uom" /></div>
              <div><Label>System</Label><Input value={form.system} onChange={(e) => setForm((f) => ({ ...f, system: e.target.value }))} placeholder="Substation" data-testid="activity-form-system" /></div>
            </div>
            <div>
              <Label>Discipline</Label>
              <Select value={form.discipline || ''} onValueChange={(v) => setForm((f) => ({ ...f, discipline: v }))}>
                <SelectTrigger data-testid="activity-form-discipline"><SelectValue placeholder="Select a discipline…" /></SelectTrigger>
                <SelectContent>
                  {DISCIPLINES.map((d) => <SelectItem key={d} value={d} data-testid={`activity-discipline-option-${d}`}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label className="mb-0">Composition (quantities only)</Label>
                <RatePicker mastersByType={mastersByType} onPick={addCompLine} />
              </div>
              {!preview.lines.length ? (
                <div className="rounded-xl border border-dashed p-6 text-center text-xs text-muted-foreground">
                  Add rate items from your Rate Master. You only set the quantity per unit — rates stay live.
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-2 py-1.5">Code</th>
                        <th className="px-2 py-1.5">Description</th>
                        <th className="px-2 py-1.5">Category</th>
                        <th className="px-2 py-1.5 text-right">Rate</th>
                        <th className="px-2 py-1.5 text-right">Qty/Unit</th>
                        <th className="px-2 py-1.5 text-right">Line cost</th>
                        <th className="w-8 px-1 py-1.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {preview.lines.map((c, idx) => (
                        <tr key={c.rateCode} className="border-b last:border-0" data-testid={`activity-comp-row-${c.rateCode}`}>
                          <td className="px-2 py-1 font-num text-[hsl(var(--copper))]">{c.rateCode}</td>
                          <td className="px-2 py-1 text-muted-foreground">{c.description || (c.resolved ? '—' : <span className="text-[hsl(var(--danger))]">unknown code</span>)}</td>
                          <td className="px-2 py-1"><span className={cn('rounded border px-1 py-0.5 text-[9px] font-semibold', catTone(c.category))}>{c.category}</span></td>
                          <td className="px-2 py-1 text-right font-num tabular-nums text-muted-foreground">{money(c.rate)}</td>
                          <td className="px-2 py-1 text-right">
                            <NumberStepper value={c.qtyPerUnit} step={1} decimals={2} min={0}
                              data-testid={`activity-comp-qty-${c.rateCode}`} inputClassName="w-16"
                              onChange={(v) => setCompQty(idx, v)} />
                          </td>
                          <td className="px-2 py-1 text-right font-num tabular-nums font-medium">{money(c.lineCost)}</td>
                          <td className="px-1 py-1">
                            <button onClick={() => removeCompLine(idx)} data-testid={`activity-comp-remove-${c.rateCode}`}
                              className="rounded p-1 text-muted-foreground hover:text-[hsl(var(--danger))]"><X size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(preview.split).map(([cat, val]) => (
                        <span key={cat} className={cn('rounded border px-1.5 py-0.5 text-[9px] font-medium', catTone(cat))}>{cat} · {money(val)}</span>
                      ))}
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Unit cost </span>
                      <span className="money text-sm font-bold" data-testid="activity-preview-unitcost">{curCode} {money(preview.unitCost)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[hsl(var(--brand))] text-white hover:brightness-110" data-testid="activity-save-btn">
              {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create activity')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent data-testid="activity-delete-dialog">
          <DialogHeader><DialogTitle>Delete {deleting?.activityCode}?</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            This removes the activity template. Tenders that already applied it keep their rows (applied by value) — they are not affected.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button className="bg-[hsl(var(--danger))] text-white hover:brightness-95" data-testid="activity-delete-confirm" onClick={doDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
